import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useRouteError } from 'react-router-dom';
import { RotateCcw, Power } from 'lucide-react';

// Inline styles for keyframe animations (no external CSS needed)
const animationStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    25% { transform: translateY(-8px) rotate(1deg); }
    75% { transform: translateY(4px) rotate(-1deg); }
  }
  @keyframes spark1 {
    0%, 100% { opacity: 0; transform: translate(0, 0) scale(0); }
    20% { opacity: 1; transform: translate(-4px, -8px) scale(1); }
    40% { opacity: 0; transform: translate(-8px, -16px) scale(0.5); }
  }
  @keyframes spark2 {
    0%, 100% { opacity: 0; transform: translate(0, 0) scale(0); }
    30% { opacity: 1; transform: translate(6px, -10px) scale(1.2); }
    60% { opacity: 0; transform: translate(10px, -20px) scale(0.3); }
  }
  @keyframes spark3 {
    0%, 100% { opacity: 0; transform: translate(0, 0) scale(0); }
    15% { opacity: 0.8; transform: translate(2px, -6px) scale(0.8); }
    50% { opacity: 0; transform: translate(-4px, -14px) scale(0.2); }
  }
  @keyframes batteryDrain {
    0% { width: 100%; background: #8bc43e; }
    30% { width: 60%; background: #8bc43e; }
    60% { width: 30%; background: #f59e0b; }
    80% { width: 12%; background: #ef4444; }
    100% { width: 6%; background: #ef4444; }
  }
  @keyframes batteryBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(139, 196, 62, 0.4); }
    50% { box-shadow: 0 0 20px 4px rgba(139, 196, 62, 0.15); }
  }
`;

function DisconnectedPlugSvg() {
  return (
    <div style={{ animation: 'float 4s ease-in-out infinite' }} className="relative">
      <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Cable coming from top */}
        <path
          d="M80 8 C80 8, 78 30, 72 42 C66 54, 60 58, 60 68"
          className="stroke-gray-400 dark:stroke-gray-500"
          strokeWidth="6"
          strokeLinecap="round"
          fill="none"
        />
        {/* Plug body */}
        <rect x="44" y="64" width="32" height="44" rx="4" className="fill-gray-600 dark:fill-gray-400" />
        {/* Plug prongs */}
        <rect x="50" y="108" width="6" height="16" rx="2" className="fill-gray-500 dark:fill-gray-300" />
        <rect x="64" y="108" width="6" height="16" rx="2" className="fill-gray-500 dark:fill-gray-300" />
        {/* Ground prong */}
        <rect x="55" y="112" width="10" height="14" rx="3" className="fill-gray-500 dark:fill-gray-300" />
        {/* Plug detail lines */}
        <line x1="50" y1="74" x2="70" y2="74" className="stroke-gray-500 dark:stroke-gray-300" strokeWidth="1.5" />
        <line x1="50" y1="80" x2="70" y2="80" className="stroke-gray-500 dark:stroke-gray-300" strokeWidth="1.5" />

        {/* Socket (separated, offset to the right) */}
        <rect x="92" y="88" width="36" height="48" rx="6" className="fill-gray-200 dark:fill-gray-700" stroke="currentColor" strokeWidth="1.5" />
        <rect x="96" y="88" width="28" height="4" rx="2" className="fill-gray-300 dark:fill-gray-600" />
        {/* Socket holes */}
        <rect x="100" y="100" width="6" height="14" rx="3" className="fill-gray-400 dark:fill-gray-500" />
        <rect x="114" y="100" width="6" height="14" rx="3" className="fill-gray-400 dark:fill-gray-500" />
        {/* Ground hole */}
        <circle cx="110" cy="122" r="5" className="fill-gray-400 dark:fill-gray-500" />

        {/* Sparks near the disconnect gap */}
        <circle cx="82" cy="100" r="2" fill="#f59e0b" style={{ animation: 'spark1 1.5s ease-in-out infinite' }} />
        <circle cx="86" cy="96" r="1.5" fill="#fbbf24" style={{ animation: 'spark2 1.8s ease-in-out infinite 0.3s' }} />
        <circle cx="79" cy="94" r="1.8" fill="#f59e0b" style={{ animation: 'spark3 2s ease-in-out infinite 0.6s' }} />
        <circle cx="84" cy="104" r="1.2" fill="#fbbf24" style={{ animation: 'spark1 2.2s ease-in-out infinite 0.9s' }} />
      </svg>
    </div>
  );
}

function BatteryIndicator() {
  return (
    <div className="flex items-center gap-2">
      {/* Battery shell */}
      <div className="relative w-16 h-7 rounded-md border-2 border-gray-400 dark:border-gray-500 overflow-hidden">
        {/* Battery fill - animates drain */}
        <div
          className="absolute left-0 top-0 h-full rounded-sm"
          style={{
            animation: 'batteryDrain 2.5s ease-out forwards, batteryBlink 1.5s ease-in-out infinite 2.5s',
          }}
        />
      </div>
      {/* Battery cap */}
      <div className="w-1.5 h-3 rounded-r-sm bg-gray-400 dark:bg-gray-500" />
    </div>
  );
}

function ErrorFallbackUI({ onReset }: { onReset: () => void }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col items-center justify-center px-6 py-10 overflow-hidden">
      <style>{animationStyles}</style>

      {/* Main content container */}
      <div className="flex flex-col items-center max-w-sm w-full" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
        {/* Illustration */}
        <div className="mb-6">
          <DisconnectedPlugSvg />
        </div>

        {/* Battery indicator */}
        <div className="mb-8">
          <BatteryIndicator />
        </div>

        {/* Messaging */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">
          Oops! We hit a bump
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-center text-sm leading-relaxed mb-8 max-w-[280px]">
          Even the best EVs need a reboot sometimes. Let's get you back on the road.
        </p>

        {/* Action buttons */}
        <div className="flex flex-col gap-3 w-full mb-6">
          <button
            onClick={() => window.location.reload()}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-semibold text-sm transition-all active:scale-[0.98]"
            style={{
              backgroundColor: '#8bc43e',
              animation: 'pulseGlow 3s ease-in-out infinite',
            }}
          >
            <Power className="w-4 h-4" />
            Restart App
          </button>
          <button
            onClick={onReset}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-medium text-sm border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm transition-all active:scale-[0.98]"
          >
            <RotateCcw className="w-4 h-4" />
            Try Again
          </button>
        </div>

        {/* Logo at bottom */}
        <div className="mt-10 opacity-40">
          <img src="/eveebuddy-logo.png" alt="EVee Buddy" className="h-8 dark:invert" />
        </div>
      </div>
    </div>
  );
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Route-level error boundary for React Router.
 * Catches errors thrown inside route components (where React Router
 * intercepts before the class-based ErrorBoundary can).
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const errorObj = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(errorObj);
  return <ErrorFallbackUI onReset={() => window.location.reload()} />;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorFallbackUI onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}
